import { Modbus as ModbusSdk, Site as SiteSdk } from "coolremote-sdk";
import useMutation from "./useMutation";
import useQuery from "./useQuery";
import { IModbusUiTemplate } from "../screens/Modbus/types";

const oid = (v: any): string | undefined =>
  typeof v === "string" ? v : v?.$oid ?? v?._id?.$oid ?? undefined;

export const normalizeUiTemplate = (doc: any): IModbusUiTemplate => ({
  ...doc,
  id: oid(doc?._id) ?? doc?.id,
  site: oid(doc?.site),
});

/**
 * BE validators are strict Joi schemas (unknown keys rejected). Send only the
 * permitted fields. Create allows `site`; update does not.
 * (validators/modbusUiTemplates.js)
 */
const serializeCreate = (t: IModbusUiTemplate) => ({
  site: t.site,
  name: t.name,
  description: t.description,
  deviceVendor: t.deviceVendor,
  deviceVendorId: t.deviceVendorId,
  data: t.data,
});

const serializeUpdate = (t: IModbusUiTemplate) => ({
  name: t.name,
  description: t.description,
  deviceVendor: t.deviceVendor,
  deviceVendorId: t.deviceVendorId,
  data: t.data,
});

export const useGetSiteModbusUiTemplates = (siteId: string | undefined, enabled = true) => {
  return useQuery({
    queryKey: ["siteModbusUiTemplates", siteId],
    queryFn: async (): Promise<IModbusUiTemplate[]> => {
      const res = await SiteSdk.getSiteModbusUiTemplates(siteId as string);
      const dict = res?.data ?? res ?? {};
      return Object.values(dict).map(normalizeUiTemplate);
    },
    onSuccess: undefined,
    onError: undefined,
    enabled: enabled && !!siteId,
  });
};

export const useGetModbusUiTemplate = (templateId: string | undefined) => {
  return useQuery({
    queryKey: ["modbusUiTemplate", templateId],
    queryFn: async (): Promise<IModbusUiTemplate> => {
      const res = await ModbusSdk.getUiTemplateById(templateId as string);
      return normalizeUiTemplate(res?.data ?? res);
    },
    onSuccess: undefined,
    onError: undefined,
    enabled: !!templateId,
  });
};

interface IMutationOptions {
  onSuccess?: (result: any) => void;
  onError?: (error: any) => void;
}

export const useCreateModbusUiTemplate = (options: IMutationOptions = {}) => {
  return useMutation({
    mutationFn: async (data: IModbusUiTemplate) => {
      const res = await ModbusSdk.createUiTemplate(serializeCreate(data));
      return normalizeUiTemplate(res?.data ?? res);
    },
    onSuccess: options.onSuccess,
    onError: options.onError,
  });
};

export const useUpdateModbusUiTemplate = (options: IMutationOptions = {}) => {
  return useMutation({
    mutationFn: async ({ templateId, data }: { templateId: string; data: IModbusUiTemplate }) => {
      const res = await ModbusSdk.updateUiTemplate(templateId, serializeUpdate(data));
      return normalizeUiTemplate(res?.data ?? res);
    },
    onSuccess: options.onSuccess,
    onError: options.onError,
  });
};

export const useRenameModbusUiTemplate = (options: IMutationOptions = {}) => {
  return useMutation({
    mutationFn: async ({ templateId, name }: { templateId: string; name: string }) => {
      const res = await ModbusSdk.renameUiTemplate(templateId, name);
      return normalizeUiTemplate(res?.data ?? res);
    },
    onSuccess: options.onSuccess,
    onError: options.onError,
  });
};

export const useDeleteModbusUiTemplate = (options: IMutationOptions = {}) => {
  return useMutation({
    mutationFn: async (templateId: string) => {
      // The DELETE body has no `.data`, so the SDK resolves `undefined`. xhr throws
      // on non-2xx, so reaching here means success — return a truthy result.
      await ModbusSdk.deleteUiTemplate(templateId);
      return { success: true };
    },
    onSuccess: options.onSuccess,
    onError: options.onError,
  });
};
